/**
 * Defensa minima de los mensajes que llegan del servidor.
 *
 * El servidor ya valida lo que entra; esto protege lo que **sale** hacia React.
 * No es un segundo sistema normativo: solo comprueba que el mensaje tiene la
 * forma que la pantalla espera, para que un payload inesperado deje un estado
 * de error legible en vez de romper el render a mitad.
 */

import type { MatchView } from '@tcg/game-rules';

/** Nombres de los mensajes, tal y como los define el servidor. */
export const ACTION_MESSAGE = 'action';
export const SNAPSHOT_MESSAGE = 'snapshot';
export const REJECTED_MESSAGE = 'actionRejected';

/** Un rechazo tal y como lo manda el servidor. */
export interface ActionRejection {
  readonly code: string;
  readonly message: string;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/**
 * Acepta un snapshot solo si trae las piezas que la pantalla usa.
 *
 * Se comprueba la estructura, no el contenido: no se recalcula ninguna regla, y
 * los tipos del dominio ya describen el resto.
 */
export function parseSnapshot(payload: unknown): MatchView | null {
  if (!isRecord(payload)) {
    return null;
  }

  const { viewer, turn, own, opponent } = payload;

  if (typeof viewer !== 'string' || !isRecord(turn) || !isRecord(own) || !isRecord(opponent)) {
    return null;
  }

  if (!Array.isArray(own.hand) || typeof opponent.handCount !== 'number') {
    return null;
  }

  return payload as unknown as MatchView;
}

/** Acepta un rechazo solo si trae codigo y mensaje. */
export function parseRejection(payload: unknown): ActionRejection | null {
  if (!isRecord(payload)) {
    return null;
  }

  const { code, message } = payload;

  if (typeof code !== 'string' || typeof message !== 'string') {
    return null;
  }

  return { code, message };
}
